/**
 * CompanyDashboardAllObjective
 *
 * Lists objectives as cards with progress and last-updated info. Opens either
 * the objective details drawer or the add-objective drawer from the bottom.
 * Fetches data when a valid year filter is present and shows an empty state when
 * no results. Uses OverlayScrollbars for consistent scroll behavior.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import Drawer from "@mui/material/Drawer";
import CompanyDashboardAllObjectiveCard from "./company-dashboard-all-objective-card/company-dashboard-all-objective-card";
import CompanyDashboardAllObjectiveDrawer from "./company-dashboard-all-objective-drawer/company-dashboard-all-objective-drawer";
import CompanyDashboardAddAllObjectiveDrawer from "./company-dashboard-all-objective-add-drawer/company-dashboard-all-objective-add-drawer";
import { getAllObjectives } from "../../../api/objectives";
import { getProgressColor } from "../company-dashboard-utils";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { useSetLoading } from "../../../common/hooks/appearance";
import "./company-dashboard-all-objective.scss";
import dayjs from "dayjs";
import NoRecordsComponent from "../../../common/components/no-records-component/no-records-component";

const CompanyDashboardAllObjective: any = (props: any) => {
  const {
    isShowAllObjectiveDrawer,
    setIsShowAllObjectiveDrawer,
    isShowAddAllObjectiveDrawer,
    setIsShowAddAllObjectiveDrawer,
    setLoading,
    status,
    year,
    employeesList,
    statusListObjectives,
    statusListKeyResults,
    statusListInitiatives,
    departmentsList,
    objectiveId,
    fetchUpcomingMilestones,
    isShowMessageLoading,
    fetchAllYearsForObjectives,
    fetchAllYearsForInitiatives,
    departments,
  } = props;
  const [objectivesData, setObjectivesData] = useState<any[]>([]);
  const [isObjectLoaded, setIsObjectLoaded] = useState<boolean>(false);
  const [objectId, setObjectId] = useState<any>(null);
  const fetchObjectives = useCallback(async () => {
    // Only proceed if both status and year are available
    if (!year) {
      return;
    }

    setIsObjectLoaded(true);
    try {
      const response = await getAllObjectives({ status, year, departments });
      setObjectivesData(response.data.data);
      setTimeout(() => setIsObjectLoaded(false), 0);
    } catch (error: any) {
      setObjectivesData([]);
      console.error(error);

      setIsObjectLoaded(false);
    }
  }, [status, year, departments, setIsObjectLoaded]);

  useEffect(() => {
    // Only call API when year is available and not empty
    if (year && year.toString().trim() !== "") {
      fetchObjectives();
    }
  }, [fetchObjectives]);

  useEffect(() => {
    if (objectiveId) {
      setObjectId(objectiveId);
    }
  }, [objectiveId]);

  const showLoading = useMemo(() => isObjectLoaded, [isObjectLoaded]);
  useSetLoading(showLoading);

  let objectivesContent: React.ReactNode = "";
  if (objectivesData?.length > 0) {
    objectivesContent = objectivesData?.map((item: any, index: number) => {
      return (
        <CompanyDashboardAllObjectiveCard
          key={index + 1}
          onClickCard={() => {
            setIsShowAllObjectiveDrawer(true);
            setObjectId(item.objectiveId);
          }}
          mainLabel={item?.objectiveName}
          progressValue={item.progress}
          subLabel={
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              <span>Last Updated</span>
              <span style={{ marginLeft: 10 }}>
                {item?.lastUpdated
                  ? dayjs(item.lastUpdated).format("MM-DD-YYYY")
                  : "-"}
              </span>
            </span>
          }
          keyResultsCount={item.keyResultsCount || 0}
          initiativesCount={item.initiativesCount || 0}
          borderColor={getProgressColor(item.status).mainColor}
          lightBorderColor={getProgressColor(item.status).lightColor}
        />
      );
    });
  } else if (!isShowMessageLoading && !isObjectLoaded) {
    objectivesContent = (
      <div className="company-dashboard-no-objective">
        <NoRecordsComponent
          title="No Objectives available."
          message="We could not find any Objectives. Please reload the page or get started by adding in a new Objective."
        />
      </div>
    );
  }

  return (
    <OverlayScrollbarsComponent
      className={`company-dashboard-all-objective ${objectivesData?.length === 1 || objectivesData?.length === 2 ? "company-dashboard-all-objective-one" : ""}`}
      options={{
        scrollbars: {
          theme: "os-theme-dark",
          autoHide: "scroll",
          autoHideDelay: 400,
        },
      }}
    >
      {objectivesContent}

      <Drawer
        anchor={"bottom"}
        open={isShowAllObjectiveDrawer}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return;
          }
          setIsShowAllObjectiveDrawer(false);
          fetchObjectives();
          fetchUpcomingMilestones({});
          fetchAllYearsForObjectives();
          fetchAllYearsForInitiatives();
          setIsShowAddAllObjectiveDrawer(false);
        }}
      >
        <CompanyDashboardAllObjectiveDrawer
          setIsShowAllObjectiveDrawer={setIsShowAllObjectiveDrawer}
          objectId={objectId}
          setAlertMessage={() => {}}
          setAlertType={() => {}}
          setAlertOpen={() => {}}
          setLoading={setLoading}
          fetchObjectives={fetchObjectives}
          employeesList={employeesList}
          statusListKeyResults={statusListKeyResults}
          statusListObjectives={statusListObjectives}
          statusListInitiatives={statusListInitiatives}
          departmentsList={departmentsList}
          fetchUpcomingMilestones={fetchUpcomingMilestones}
          fetchAllYearsForObjectives={fetchAllYearsForObjectives}
          fetchAllYearsForInitiatives={fetchAllYearsForInitiatives}
        />
      </Drawer>
      <Drawer
        anchor={"bottom"}
        open={isShowAddAllObjectiveDrawer}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return;
          }
          setIsShowAllObjectiveDrawer(false);
          fetchObjectives();
          fetchUpcomingMilestones({});
          fetchAllYearsForObjectives();
          fetchAllYearsForInitiatives();
          setIsShowAddAllObjectiveDrawer(false);
          setIsShowAddAllObjectiveDrawer(false);
          setObjectId(null);
        }}
      >
        <CompanyDashboardAddAllObjectiveDrawer
          setIsShowAllObjectiveDrawer={setIsShowAddAllObjectiveDrawer}
          setAlertMessage={() => {}}
          setAlertType={() => {}}
          setAlertOpen={() => {}}
          setLoading={setLoading}
          fetchObjectives={fetchObjectives}
          employeesList={employeesList}
          statusListKeyResults={statusListKeyResults}
          statusListObjectives={statusListObjectives}
          statusListInitiatives={statusListInitiatives}
          departmentsList={departmentsList}
          objectId={objectId}
          fetchUpcomingMilestones={fetchUpcomingMilestones}
          fetchAllYearsForObjectives={fetchAllYearsForObjectives}
          fetchAllYearsForInitiatives={fetchAllYearsForInitiatives}
        />
      </Drawer>
    </OverlayScrollbarsComponent>
  );
};

export default CompanyDashboardAllObjective;
